import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { MarkdownRenderer } from '../ui'
import { formatDate } from '../../lib/dates'
import Logo from '../ui/Logo'

interface ReportPrintSheetProps {
  title: string
  summary: string | null
  content: string
  publishedAt: string | null
  versionNumber: number | null
  workspaceName?: string
}

/** Text safe to put between the quotes of a CSS string. */
function cssString(value: string): string {
  return value.replace(/[\\"]/g, '\\$&').replace(/[\r\n]+/g, ' ')
}

/**
 * The branded version of a report, drawn only on paper. It lives in `body`
 * beside the app root (a portal) so the print stylesheet can drop the whole
 * app and keep just this, and it is light-themed on its own terms: the page
 * switches the theme off while printing, see `DownloadReportPdf`.
 */
export function ReportPrintSheet({
  title,
  summary,
  content,
  publishedAt,
  versionNumber,
  workspaceName,
}: ReportPrintSheetProps) {
  const { t, i18n } = useTranslation()

  const meta = [
    workspaceName,
    publishedAt ? t('report.publishedOn', { date: formatDate(publishedAt, i18n.language) }) : null,
    versionNumber ? `v${versionNumber}` : null,
  ].filter(Boolean)

  const footer = [`ScaleBrain · ${t('report.pdf.confidential')}`, workspaceName]
    .filter(Boolean)
    .join(' · ')

  return createPortal(
    <div className="report-print-sheet bg-white text-gray-900" data-testid="report-print-sheet">
      <header className="mb-8 border-b border-primary-600 pb-4">
        <div className="flex items-center gap-2.5">
          <Logo />
        </div>
        <h1 className="mt-6 font-display text-display leading-tight text-primary-600">{title}</h1>
        {meta.length > 0 && <p className="mt-2 text-sm text-gray-500">{meta.join(' · ')}</p>}
        {summary && <p className="mt-4 text-base leading-relaxed text-gray-700">{summary}</p>}
      </header>

      <MarkdownRenderer content={content} />

      {/* A margin box repeats on every page, which a fixed element does not. */}
      <style>{`@media print { @page { @bottom-left { content: "${cssString(footer)}"; font-size: 9pt; color: #6b7280; } } }`}</style>
    </div>,
    document.body,
  )
}
